import React, { EffectCallback, Fragment, ReactElement, useEffect } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

// Components

import AlertInfo from '@components/common/alert/AlertInfo';
import Form from '@lib/wrappers/Form';
import InstructionalText from '../notifications/InstructionalText';
import NamesInputs from './pageIncludes/common/names/NamesInputs';
import PageHeader from '../headers/PageHeader';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';
import { ConditionalRendering } from '@components/common/conditional-rendering';

// Action creators

import {
    changeData,
    clearMethods,
    clearScrollToElement,
    fetchAllBloodRelationships,
    fetchEnrollments,
    removeCachedData,
    scrollToElement,
    setMethods
} from '@actions/ssap/ssapActionCreators';

import { showModal } from '@actions/common/actionCreators';

// Selectors

import { selectIfShouldFetchEnrollments, selectNonSeekingForCoverageAndEnrolled } from '@reducers/appReducer';

// Utils

import { analyzeTaxHouseholdComposition } from '@utils/householdMember/analyzeTaxHouseholdComposition';
import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import {
    dayjsUtil,
    getDateFromYear,
    isDateAfter,
    isDateBefore,
} from '@utils/dates/date.utils';

// Constants

import { configs } from '@constants/configs/configs';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { ssapModalTypes as MODAL_TYPES } from '@constants/ssap/modals/ssapModalTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';

// Types

import {
    ApplicationStore,
    BloodRelationship,
    EmployerSponsoredCoverage,
    FamilyStatus,
    HouseholdMember,
    HouseholdMembers,
    HraDetails,
    MemberRemovalReason,
    OnChangeArgTypes,
    PersonName,
    RemovedMembers,
    SsapData,
    SsapPage,
    SsapReduxActions
} from '@components/ssap/SsapTypes';


/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'About Household' Page
 *
 */
const Page1_05 = (props: SsapPage): ReactElement => {

    const {
        data,
        data: {
            coverageYear
        },
        environment: {
            isState,
            links: {
                PHONE
            },
            localDates: {
                serverDate
            }
        },
        householdMembers = [],
        page,
        relations,
        relations: {
            loadedRelationsWholeList = []
        },
        scroll,
        taxHousehold,
        taxHousehold: {
            bloodRelationship,
            primaryTaxFilerPersonId
        }
    } = props;

    const {
        addressDefaults: {
            defaultMailingAddressId
        },
        buttonContainer: ButtonContainer,
        formName,
        itfFeatureEnabled,
        memberRemovalReasonEnabled,
        pageName
    } = props;

    const {
        applicantGuid: primaryContactPersonApplicantGuid,
        householdContact: {
            homeAddress: primaryContactHomeAddress,
            outOfStateAttestation
        }
    } = householdMemberTools.getPrimaryContact(householdMembers);

    const family: FamilyStatus = analyzeTaxHouseholdComposition(
        householdMembers,
        relations,
        bloodRelationship,
        serverDate,
        primaryTaxFilerPersonId
    );

    const intlApi: IntlShape = useIntl();

    const shouldFetchEnrollments = useSelector(selectIfShouldFetchEnrollments);
    const nonSeekingForCoverageAndEnrolled = useSelector(selectNonSeekingForCoverageAndEnrolled);

    useEffect((): void => {
        shouldFetchEnrollments && props.actions.fetchEnrollments();
        loadedRelationsWholeList.length === 0 && props.actions.fetchAllBloodRelationships();
    }, [ ]);

    useEffect((): void => {
        if(outOfStateAttestation) {
            const newState: SsapData = { ...data };
            newState.taxHousehold.householdMember = householdMemberTools.updatePrimaryContact(
                newState.taxHousehold.householdMember,
                { applyingForCoverageIndicator: false }
            );
            props.actions.changeData(newState);
        }
    }, [ householdMembers.length ]);

    // Here we scroll to the top of the page if we have not had element to be scrolled

    useEffect((): void => {
        if(scroll === null) {
            document.body.scrollTop = document.documentElement.scrollTop = 0;
        }
    }, [ ]);

    // Here we scroll to created household member

    const elementToBeScrolledTo: Element | null = document.querySelector(`.${ scroll }`);

    useEffect(() => {
        if(scroll !== null && elementToBeScrolledTo) {
            elementToBeScrolledTo.scrollIntoView({ behavior: 'smooth' });
            props.actions.clearScrollToElement();
        }
    }, [ scroll, elementToBeScrolledTo ]);


    // If we clicked 'Add another person' on Summary page for 1st section here we:
    // a) generate new One;
    // b) increase last household member index to scroll to him/her

    const handleAddingNewPerson = (): void => {

        const newHouseholdMember: HouseholdMember = cloneObject({
            ...EMPTY_OBJECTS.HOUSEHOLD_MEMBER,
            ...(
                configs.ENV.DEVELOPMENT
                    ? {
                        applicantGuid: String(
                            Math.max.apply(
                                null,
                                householdMembers.map((hhm: HouseholdMember) => +hhm.applicantGuid)
                            ) + 1
                        )
                    }
                    : { }
            ),
            personId: householdMembers.length + 1,
            householdContact: {
                ...EMPTY_OBJECTS.HOUSEHOLD_MEMBER.householdContact,
                homeAddress: primaryContactHomeAddress
            },
            ...(
                itfFeatureEnabled
                    ? { mailingAddressId: defaultMailingAddressId }
                    : { }
            )
        });

        const newState: SsapData = {
            ...data,
            maxAchievedPage: page
        };

        const updatedHouseholdMembers: HouseholdMembers = [
            ...householdMembers,
            newHouseholdMember
        ];

        newState.taxHousehold.householdMember = updatedHouseholdMembers;
        props.actions.changeData(newState);
        props.actions.scrollToElement(`household-member-${updatedHouseholdMembers.length - 1}`);
    };

    const handleMemberDeletion = (
        hhms: HouseholdMembers,
        memberIndex: number,
        removedMember: RemovedMembers = { }
    ): void => {

        const bloodRelationships = cloneObject(bloodRelationship);
        const householdMembers = cloneObject(hhms);
        const ssapData = cloneObject(data);

        // 0. Here we create Map with match [ <personId> before deletion, <personId> after deletion ]

        const personIdsMatch: Map<number, number | null> = householdMembers.reduce(
            (
                res: Map<number, number | null>,
                hhm: HouseholdMember,
                idx: number
            ): Map<number, number | null> => {
                switch(true) {
                    case memberIndex > idx:
                        return res.set(idx + 1, idx + 1);
                    case memberIndex === idx:
                        return res.set(idx + 1, null);
                    case memberIndex < idx:
                        return res.set(idx + 1, idx);
                    default:
                        return res;
                }
            },
            new Map([ ])
        );

        // 1. Here we delete household member

        householdMembers.splice(memberIndex, 1);

        const remainedGuids: Array<string | null> = householdMembers.map((hhm: HouseholdMember) => hhm.applicantGuid);

        // 1.1. Here we normalize consequences due to gap after deletion:

        // 2. Here we adjust blood relationship between rest of household members
        // 2.1. Filtering we exclude all relations of deleted member
        // 2.2. Mapping we adjust household members' indexes to eliminate gap in number sequence

        const refinedBloodRelationship = bloodRelationships
            .filter((br: BloodRelationship): boolean =>
                [ br.personGuid, br.relatedPersonGuid ]
                    .every((guid: string): boolean => remainedGuids.includes(guid)))
            .map((br: BloodRelationship): BloodRelationship => ({
                ...br,
                individualPersonId: String(personIdsMatch.get(+br.individualPersonId)),
                relatedPersonId: String(personIdsMatch.get(+br.relatedPersonId))
            }));

        // 3. Here we update JSON in Redux store

        const newState: SsapData = {
            ...ssapData,
            maxAchievedPage: page,
            removedMembers: { ...ssapData.removedMembers, ...removedMember }
        };

        newState.taxHousehold = {
            ...newState.taxHousehold,
            bloodRelationship: refinedBloodRelationship,
            primaryTaxFilerPersonId: 0,
            // @ts-ignore
            householdMember: householdMembers.map((hhm: HouseholdMember) => {
                return {
                    ...hhm,
                    personId: personIdsMatch.get(hhm.personId),
                    employerSponsoredCoverage: hhm.employerSponsoredCoverage
                        .filter((c: EmployerSponsoredCoverage): boolean => remainedGuids.includes(c.applicantGuid)),
                    healthReimbursementArrangement: hhm.healthReimbursementArrangement
                        .filter((hra: HraDetails): boolean => remainedGuids.includes(hra.applicantGuid)),
                    householdId: 0,
                    taxHouseholdComposition: {
                        ...hhm.taxHouseholdComposition,
                        ...EMPTY_OBJECTS.HOUSEHOLD_COMPOSITION
                    }
                };
            })
        };

        props.actions.changeData(newState);
        props.actions.removeCachedData(Object.keys(removedMember)[0]);
    };

    // Sets applyingForCoverageIndicator to false and executes clean up action from RemoveMember modal

    const handleRemovingApplyingForCoverage = (...args: OnChangeArgTypes): void => {
        const [ householdMembers, hhmIndex ] = args;

        const updatedHhm = householdMembers[hhmIndex];

        updatedHhm.applyingForCoverageIndicator = false;

        onFieldChange_coverage(updatedHhm, hhmIndex, [], [ '', false ]);
    };

    // Open dialog modal for member removing

    type OpenRemoveMemberModalArgs = [
        PersonName,
        string | null,
        string | null,
        boolean | null,
        boolean | null,
        string | null,
        HouseholdMember,
        number
    ]

    const openRemoveMemberModal = (...args: OpenRemoveMemberModalArgs): void => {
        const [
            name,
            applicantGuid,
            dateOfBirth,
            applyingForCoverageIndicator,
            deceased,
            deceasedDate,
            ...restArgs] = args;

        if(applicantGuid === null) {
            handleMemberDeletion.apply(null, [ ...restArgs, { } ]);
            return;
        }

        props.actions.showModal({
            modalType: MODAL_TYPES.REMOVE_MEMBER_MODAL,
            modalData: {
                actions: {
                    handleRemovingApplyingForCoverage: () => handleRemovingApplyingForCoverage.apply(null, restArgs),
                    handleMemberDeletion: (...argsFromModal: [ RemovedMembers ]) => handleMemberDeletion.apply(null, [ ...restArgs, ...argsFromModal ]),
                },
                applicantGuid,
                applyingForCoverageIndicator,
                dateOfBirth,
                deceased,
                deceasedDate,
                memberRemovalReasonEnabled,
                name,
                serverDate
            }
        });
    };

    // Updating some props in JSON

    const onFieldChange = (...args: OnChangeArgTypes): void => {
        let [ newData, indexInArray, , [ name, value ] ] = args;

        const newState: SsapData = { ...data };

        const {
            applicantGuid,
            dateOfBirth,
            name: personName,
            specialCircumstances: {
                deceased,
                deceasedDate
            }
        } = newData;

        if(
            name === 'deceasedDate' &&
            value &&
            applicantGuid !== primaryContactPersonApplicantGuid
        ) {

            const isDeceasedEarlier: boolean = !!deceased &&
                isDateBefore(deceasedDate || dayjsUtil(), getDateFromYear(coverageYear), 'year');

            if(isDeceasedEarlier) {
                props.actions.showModal({
                    modalType: MODAL_TYPES.DECEASED_MEMBER_MODAL,
                    modalData: {
                        actions: {
                            handleMemberDeletion: (): void => {
                                handleMemberDeletion.apply(
                                    null,
                                    [
                                        householdMembers,
                                        indexInArray,
                                        {
                                            [ applicantGuid ]: {
                                                demographic: {
                                                    birthDate: dateOfBirth,
                                                    deathDate: deceasedDate,
                                                    divorceDate: null,
                                                    name: personName,
                                                    otherDate: null
                                                },
                                                removalReasonType: MemberRemovalReason.DEATH
                                            }
                                        }
                                    ]
                                );
                            },
                        },
                        coverageYear,
                        personName
                    }
                });
            }
        }

        if(name === 'deceased' && !value) {
            newData = {
                ...newData,
                specialCircumstances: {
                    ...newData.specialCircumstances,
                    deceasedDate: null
                }
            };
        }

        newState.taxHousehold.householdMember[indexInArray] = newData;
        props.actions.changeData(newState);
    };

    const onFieldChange_coverage = (...args: OnChangeArgTypes): void => {

        let [ newData, indexInArray, , [ , value ] ] = args;

        const newState: SsapData = { ...data };

        if(value) {
            newData = {
                ...newData,
                specialCircumstances: {
                    ...newData.specialCircumstances,
                    deceased: null,
                    deceasedDate: null
                }
            };
        } else {
            newState
                .taxHousehold
                .householdMember[ indexInArray ]
                .citizenshipImmigrationStatus
                .citizenshipStatusIndicator = null;
        }

        newState.taxHousehold.householdMember[indexInArray] = newData;

        props.actions.changeData(newState);
    };

    useEffect((): ReturnType<EffectCallback> => {
        props.actions.setMethods('Page1_05', {
            handleAddingNewPerson,
            handleRemovingApplyingForCoverage,
            handleMemberDeletion,
            openRemoveMemberModal
        });
        return (): void => {
            props.actions.clearMethods('Page1_05');
        };
    }, [ householdMembers.length ]);

    return (
        <Fragment>
            <div id = 'saPrimaryContact' >
                <ConditionalRendering isTrue={!isState.NM}>
                    <InstructionalText
                        anchor = 'instructional_text.about_household.anchor'
                        Component = {
                            <p data-instructions = 'instructional_text.about_household.main_part' />
                        }
                    />
                </ConditionalRendering>
                <Form { ...{ formName, pageName } } >
                    {
                        householdMembers.map((member: HouseholdMember, i: number): ReactElement => {
                            const {
                                applicantGuid,
                                dateOfBirth,
                                name,
                                name: {
                                    firstName,
                                    middleName,
                                    lastName,
                                    suffix
                                },
                                householdContact: {
                                    outOfStateAttestation
                                },
                                applyingForCoverageIndicator,
                                personId,
                                specialCircumstances: {
                                    deceased,
                                    deceasedDate
                                }
                            } = member;

                            const isEnrolledVisible: boolean = nonSeekingForCoverageAndEnrolled.includes(applicantGuid);

                            const isClaimedByITF: boolean = itfFeatureEnabled
                                ? !!family
                                    .dependents
                                    .find(d => d.dependentPersonId === personId && d.isClaimedByITF)
                                : false;

                            return (
                                <div
                                    className = { `household-member-${ i } subsection` }
                                    key = { `${ applicantGuid }_${ i }` }
                                >
                                    <PageHeader
                                        text = 'ssap.subheader.non_internationalized_text'
                                        textReplacements = { {
                                            text: firstName
                                                ? normalizeNameOfPerson(name)
                                                : (
                                                    <FormattedMessage
                                                        id = 'common.applicant'
                                                        values = { { index: i + 1 } }
                                                    />
                                                )
                                        } }
                                        headerClassName = 'gi-heading-alt'
                                        additionalElement = {
                                            <ButtonContainer
                                                type = 'removeMemberButton'
                                                args = { [
                                                    name,
                                                    applicantGuid,
                                                    dateOfBirth,
                                                    applyingForCoverageIndicator,
                                                    deceased,
                                                    deceasedDate,
                                                    householdMembers,
                                                    i
                                                ] }
                                                {
                                                    ...(
                                                        i === 0
                                                            ? {
                                                                isDisabled: true,
                                                                tooltipContent: 'ssap.tooltip.remove_member_button',
                                                                tooltipContentReplacement: { phoneNumber: PHONE },
                                                                withTooltip: true
                                                            }
                                                            : { }
                                                    )
                                                }
                                            />
                                        }
                                        additionalElementClassName = 'header-btn'
                                    />

                                    <ValidationErrorMessage
                                        isVisible = { isEnrolledVisible }
                                        messageText = { VALIDATION_ERROR_MESSAGES.APPLICANT_ENROLLED }
                                        errorClasses={ [ 'margin-l-0', 'margin-b-10', 'width-full' ] }
                                    />

                                    <UniversalInput
                                        legend = { {
                                            legendText: 'ssap.input.legend.seeking_for_coverage',
                                            dataInstructionsRightSide: outOfStateAttestation && 'ssap.input.legend.seeking_for_coverage.out_of_state'
                                        } }
                                        label = { {
                                            showRequiredIcon: true,
                                            labelClass: [ 'tablet:grid-col-4 gi-radio_buttons gi-grid__layout-inline gi-col-layout__legend padding-r-1' ],
                                            ulClass: [ 'tablet:grid-col-6 gi-grid__layout-inline' ],
                                        } }
                                        inputData = { {
                                            isInputRequired: true,
                                            type: INPUT_TYPES.RADIO_BUTTONS,
                                            liClass: [ 'mobile-lg:grid-col gi-grid__layout-inline' ],
                                            name: 'applyingForCoverageIndicator',
                                            value: applyingForCoverageIndicator,
                                            options: (outOfStateAttestation || isClaimedByITF)
                                                ? DROPDOWN_OPTIONS.YES_NO_DISABLED
                                                : DROPDOWN_OPTIONS.YES_NO,
                                            validationType: VALIDATION_TYPES.CHOSEN,
                                            currentData: householdMembers[i],
                                            fields: [ ]
                                        }}
                                        errorClasses = { [ 'margin-l-0' ] }
                                        inputActions = { {
                                            onFieldChange: onFieldChange_coverage
                                        } }
                                        indexInArray = { i }
                                    />
                                    <NamesInputs
                                        {...{
                                            firstName,
                                            middleName,
                                            lastName,
                                            suffix,
                                            currentData: member,
                                            fields: [ 'name' ],
                                            isRequired: true,
                                            inputActions: {
                                                onFieldChange
                                            },
                                            underFormControl: true,
                                            isInputStandalone: true,
                                            indexInArray: i
                                        }}
                                    />
                                    <UniversalInput
                                        label = { {
                                            text: 'ssap.input.label.birth_date',
                                            labelClass: [ 'tablet:grid-col-4 gi-grid__layout-inline gi-col-layout__label' ],
                                        }}
                                        inputData = { {
                                            divClass: [ 'tablet:grid-col-6' ],
                                            isInputRequired: true,
                                            type: INPUT_TYPES.DATE_PICKER,
                                            name: 'dateOfBirth',
                                            value: dateOfBirth,
                                            valuesToMatch: serverDate,
                                            validationType: VALIDATION_TYPES.DATE_NOT_GREATER_THAN_CURRENT,
                                            currentData: householdMembers[i],
                                            fields: [ ]
                                        }}
                                        errorClasses = { [ 'grid-offset-4' ] }
                                        inputActions = { {
                                            onFieldChange
                                        } }
                                        indexInArray = { i }
                                    />

                                    {
                                        applyingForCoverageIndicator === false &&

                                        <Fragment>
                                            <UniversalInput
                                                label = { {
                                                    text: 'ssap.input.label.deceased'
                                                } }
                                                inputData = { {
                                                    isInputRequired: false,
                                                    type: INPUT_TYPES.CHECKBOX,
                                                    name: 'deceased',
                                                    classes: [ 'tablet:grid-col-6 tablet:grid-offset-4 layout--02' ],
                                                    checked: deceased,
                                                    value: deceased,
                                                    currentData: householdMembers[i],
                                                    fields: [ 'specialCircumstances' ]
                                                } }
                                                inputActions = { {
                                                    onFieldChange: onFieldChange
                                                } }
                                                indexInArray = { i }
                                            />

                                            {
                                                deceased &&

                                                <UniversalInput
                                                    label = { {
                                                        text: 'ssap.input.label.death_date',
                                                        labelClass: [ 'tablet:grid-col-4 gi-grid__layout-inline gi-col-layout__label' ],
                                                    } }
                                                    inputData = { {
                                                        divClass: [ 'tablet:grid-col-6' ],
                                                        isInputRequired: true,
                                                        type: INPUT_TYPES.DATE_PICKER,
                                                        name: 'deceasedDate',
                                                        value: deceasedDate,
                                                        valuesToMatch: [ dateOfBirth, serverDate ],
                                                        validationType: VALIDATION_TYPES.DATE_BETWEEN_TWO_DATES,
                                                        validationErrorMessage: isDateAfter(deceasedDate || dayjsUtil(), serverDate, 'day')
                                                            ? VALIDATION_ERROR_MESSAGES.DECEASED_DATE_NOT_FUTURE_DATE
                                                            : VALIDATION_ERROR_MESSAGES.DATE,
                                                        currentData: householdMembers[i],
                                                        fields: [ 'specialCircumstances' ]
                                                    } }
                                                    errorClasses = { [ 'grid-offset-4' ] }
                                                    inputActions = { {
                                                        onFieldChange
                                                    } }
                                                    indexInArray = { i }
                                                />
                                            }
                                        </Fragment>
                                    }

                                </div>
                            );
                        })
                    }
                </Form>
                <div className = 'subsection'>
                    <PageHeader
                        text = 'ssap.subheader.need_include_someone_else'
                        headerClassName = 'gi-heading-alt'
                        additionalElement = {(
                            <ButtonContainer
                                type = 'addPersonButton'
                                args = { [ householdMembers ] }
                            />
                        )}
                        additionalElementClassName = 'header-btn'
                    />
                    <AlertInfo id = 'ssap.start.about_your_household.include_someone_else_alert' />
                </div>
            </div>
        </Fragment>
    );
};

const mapStateToProps = (state: ApplicationStore): Partial<SsapPage> => ({
    environment: state.common.environment,

    data: state.ssap.data,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,

    page: state.ssap.page,

    relations: state
        .ssap
        .relations,

    scroll: state.ssap.scroll,
    taxHousehold: state.ssap.data.taxHousehold
});

const mapDispatchToProps = (dispatch: Dispatch): { actions: Partial<SsapReduxActions> } => ({
    actions: bindActionCreators({
        changeData,
        clearMethods,
        clearScrollToElement,
        fetchAllBloodRelationships,
        fetchEnrollments,
        removeCachedData,
        scrollToElement,
        setMethods,
        showModal
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page1_05);
